import { Box, Button, Typography } from "@mui/material";
import _ from "lodash";
import { useState, useCallback, useMemo, useEffect } from "react";

import LoadingIndicator from "../../global/LoadingIndicator";
import ResponseModal from "../../global/ResponseModal";
import HNSColors from "../../ui library/HNSColors";
import RenderQuestion from "../FollowUps/followup-form/modules/DynamicSection/components/RenderQuestion";

const DynamicForm = ({
  dynamicForm,
  onSubmit,
  isLoading = false,
  isSubmitting = false,
  successMessage = null,
  errorMessage = null,
  onCloseSuccess = null,
  onCloseError = null,
}) => {
  const [formData, setFormData] = useState({});
  const [initialData, setInitialData] = useState({});

  // Memoize filtered questions to avoid unnecessary re-renders
  const filteredQuestions = useMemo(() => {
    if (!dynamicForm?.sections) {
      return [];
    }

    return dynamicForm.sections.flatMap(({ header, questions }) => {
      const sectionId = header.label.toLowerCase().replaceAll(" ", "-");
      return Object.entries(questions || {})
        .filter(([_, item]) => typeof item === "object" && item.module)
        .map(([questionKey, question], qIdx) => ({
          questionKey,
          question,
          qIdx,
          sectionId,
        }));
    });
  }, [dynamicForm]);

  // Validate if form has valid data
  const isFormValid = useMemo(() => {
    return !Object.keys(initialData).every((key) => _.isEqual(initialData[key], formData[key]));
  }, [formData]);
  useEffect(() => {
    const newInitialData = {};
    if (dynamicForm?.sections) {
      dynamicForm.sections.forEach((section) => {
        Object.entries(section?.questions).forEach(([key, value]) => {
          newInitialData[key] = value?.data;
        });
      });
    }
    setInitialData(newInitialData);
    setFormData(newInitialData);
  }, [dynamicForm]);

  const handleSubmit = useCallback(() => {
    if (!isFormValid || isSubmitting) {
      return;
    }
    onSubmit(formData);
  }, [formData, onSubmit, isFormValid, isSubmitting]);

  const handleAnswerChange = useCallback((questionKey, value) => {
    setFormData((prev) => {
      const updatedFormData = { ...prev };

      if (value === undefined || (Array.isArray(value) && value.length === 0)) {
        delete updatedFormData[questionKey];
      } else {
        updatedFormData[questionKey] = value;
      }
      return updatedFormData;
    });
  }, []);

  const handleModalClose = useCallback(
    (open) => {
      if (!open) {
        if (errorMessage) {
          onCloseError?.();
        } else if (successMessage) {
          onCloseSuccess?.();
        }
      }
    },
    [errorMessage, successMessage, onCloseError, onCloseSuccess]
  );

  if (isLoading) {
    return (
      <Box className="flex h-64 items-center justify-center">
        <LoadingIndicator />
      </Box>
    );
  }

  if (!dynamicForm?.sections) {
    return (
      <Box className="flex h-64 items-center justify-center">
        <Typography variant="h6" color="textSecondary">
          No form data found
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="flex h-full w-full flex-col p-4">
      <Box className="flex w-full flex-wrap gap-4">
        {filteredQuestions.map(({ questionKey, question, qIdx, sectionId }) => (
          <RenderQuestion
            key={questionKey}
            question={question}
            index={qIdx}
            sectionId={sectionId}
            questionKey={questionKey}
            handleAnswerChange={handleAnswerChange}
          />
        ))}
      </Box>

      <Box className="flex justify-end pt-4">
        <Button
          variant="contained"
          color="primary"
          sx={{
            background: HNSColors.secondary[500],
            color: `${HNSColors.neutrals[900]} !important`,
            borderRadius: "500px",
            textTransform: "none",
            fontWeight: 400,
            fontSize: "14px",
            boxShadow: "none",
            "&:hover": { background: HNSColors.secondary[600] },
            minWidth: "180px",
            height: "40px",
          }}
          onClick={handleSubmit}
          disabled={!isFormValid || isSubmitting}
        >
          Save
        </Button>
      </Box>

      {/* Response Modal for success and error states */}
      <ResponseModal
        title={
          errorMessage
            ? `${dynamicForm?.formHeader?.action_type__label} Form`
            : "Successfully saved!"
        }
        description={
          errorMessage ? errorMessage : successMessage || "Changes were saved successfully"
        }
        isError={!!errorMessage}
        openBool={!!successMessage || !!errorMessage}
        setOpenBool={handleModalClose}
        errorMessage={errorMessage ? [errorMessage] : []}
      />
    </Box>
  );
};

export default DynamicForm;
